'use client'

import { useCookieValue } from '@react-hookz/web/useCookieValue/index.js'

import { useMemo } from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'

import type { ExtendedUser } from '../types'

import { env } from '../helpers'
import { createLogger, getPrefix } from '../helpers/log'
import { usePathname } from '../helpers/translations'
import useUser from './useUser'

export default function useLogger<T extends ExtendedUser = ExtendedUser>(
  customContext: Record<string, unknown> = {},
  transformUser: ((user: T) => Record<string, unknown>) | undefined = undefined,
) {
  const pathname = usePathname()
  const actualPathname = useMemo(
    () =>
      isEmpty(customContext?.pathname)
        ? pathname
        : (customContext?.pathname as string),
    [customContext, pathname],
  )

  const [gdprConsent] = useCookieValue('gdpr-consent')

  const prefix = useMemo(
    () =>
      isEmpty(customContext?.prefix)
        ? getPrefix(actualPathname)
        : (customContext?.prefix as string),
    [customContext, actualPathname],
  )

  const { isLoading, user } = useUser<T>()

  const context = useMemo(
    () => ({
      ...customContext,
      env: env(),
      gdprConsent,
      prefix,
      user: {
        isLoading,
        ...(isFunction(transformUser) && !isEmpty(user)
          ? transformUser(user)
          : { user: user ?? null }),
      },
    }),
    [customContext, isLoading, prefix, transformUser, user, gdprConsent],
  )

  const logger = useMemo(
    () => createLogger(actualPathname, context, gdprConsent === 'true'),
    [actualPathname, context, gdprConsent],
  )

  return logger
}
